import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, local, setToken, setKind, getToken, getKind, logoSrc } from "@/lib/api";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [org, setOrg] = useState(null);
  const [paywall, setPaywall] = useState({ enabled: false, plan_required: "standard" });
  const [loading, setLoading] = useState(true);

  const loadStaff = useCallback(async () => {
    const me = (await api.get("/auth/me")).data;
    setUser({ ...me, kind: "staff" });
    try {
      const [o, pw] = await Promise.all([
        api.get("/org").then((r) => r.data).catch(() => null),
        local.get("/paywall/me").then((r) => r.data).catch(() => null),
      ]);
      if (o) setOrg(o);
      if (pw) setPaywall(pw);
    } catch (_) {}
    return me;
  }, []);

  const loadPatient = useCallback(async () => {
    const me = (await api.get("/portal/me")).data;
    setUser({ ...me, kind: "patient", role: "patient" });
    return me;
  }, []);

  const bootstrap = useCallback(async () => {
    if (!getToken()) { setLoading(false); return; }
    setLoading(true);
    try {
      if (getKind() === "patient") await loadPatient();
      else await loadStaff();
    } catch (_) {
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [loadStaff, loadPatient]);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  // Re-check paywall state when the tab regains focus, so a developer activating
  // the paywall is reflected without requiring a full re-login.
  useEffect(() => {
    const onFocus = () => {
      if (getToken() && getKind() === "staff") {
        local.get("/paywall/me").then((r) => setPaywall(r.data)).catch(() => {});
      }
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  // Favicon sync: use the signed-in org's logo as the browser tab icon (white-labelling).
  useEffect(() => {
    let link = document.querySelector("link[rel='icon']");
    if (!link) { link = document.createElement("link"); link.rel = "icon"; document.head.appendChild(link); }
    link.href = org?.logo ? logoSrc(org.logo) : "/favicon-32.png";
  }, [org]);

  const afterAuth = async (data, kind) => {
    setToken(data.token);
    setKind(kind);
    if (kind === "patient") await loadPatient();
    else await loadStaff();
  };

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    await afterAuth(data, "staff");
    return data.user;
  };
  const registerOrg = async (payload) => {
    const { data } = await api.post("/auth/register-organisation", payload);
    await afterAuth(data, "staff");
    return data;
  };
  const registerStaff = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    await afterAuth(data, "staff");
    return data;
  };
  const patientLogin = async (payload) => {
    const { data } = await api.post("/patient/login", payload);
    await afterAuth(data, "patient");
    return data;
  };
  const patientRegister = async (payload) => {
    const { data } = await api.post("/patient/register", payload);
    await afterAuth(data, "patient");
    return data;
  };
  const logout = () => {
    api.post("/auth/logout").catch(() => {});
    setToken(null);
    setKind("staff");
    setUser(null);
    setOrg(null);
    window.location.href = "/login";
  };

  const refreshPaywall = useCallback(async () => {
    const pw = await local.get("/paywall/me").then((r) => r.data).catch(() => null);
    if (pw) setPaywall(pw);
  }, []);

  const has = (perm) => !perm || (user?.permissions || []).includes(perm) || user?.role === "superadmin";

  const value = {
    user, org, paywall, loading, has,
    login, registerOrg, registerStaff, patientLogin, patientRegister, logout,
    reload: bootstrap, refreshPaywall, setOrg,
  };
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}
